import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IndianRupee,
  TrendingUp,
  Receipt,
  Search,
  RotateCcw,
  ShieldCheck,
  Package,
  Calendar,
  CheckCircle2,
  Clock,
  Coins,
  ArrowUpRight,
  ShoppingCart,
  FileText,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  type DataTableColumn,
  type DataTablePaginationMeta,
} from "@/components/shared";
import {
  channelOrdersService,
  type ChannelCommissionSummary,
} from "@/services/channelOrdersService";
import { useChannelDesk } from "@/lib/channel-desk";

type CommissionEntry = ChannelCommissionSummary["entries"][number];

function formatInr(amount: number) {
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

export default function DistributorCommission() {
  const desk = useChannelDesk();
  const queryClient = useQueryClient();

  const catalogueUrl = desk.base === "/store/supply" ? "/store/supply" : `${desk.base}/catalogue`;
  const ordersUrl = desk.base === "/store/supply" ? "/store/supply/orders" : `${desk.base}/orders`;

  // Search & Filter State
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch commission summary
  const commissionQuery = useQuery({
    queryKey: ["channel", "commission", desk.base],
    queryFn: () => channelOrdersService.commission(),
  });

  const data = commissionQuery.data;
  const balance = data?.balance ?? 0;
  const rawEntries = useMemo(() => data?.entries ?? [], [data?.entries]);

  // Client search filter on entries
  const filteredEntries = useMemo(() => {
    if (!appliedSearch) return rawEntries;
    const term = appliedSearch.trim().toLowerCase();
    return rawEntries.filter((e) => {
      return (
        String(e.id).includes(term) ||
        (e.channelOrderId && String(e.channelOrderId).includes(term)) ||
        (e.productName && e.productName.toLowerCase().includes(term)) ||
        (e.description && e.description.toLowerCase().includes(term))
      );
    });
  }, [rawEntries, appliedSearch]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [appliedSearch]);

  // Statistics
  const stats = useMemo(() => {
    const totalCredited = rawEntries.reduce((sum, e) => sum + (e.amount > 0 ? e.amount : 0), 0);
    const transactionCount = rawEntries.length;
    return { totalCredited, transactionCount };
  }, [rawEntries]);

  // Pagination calculation
  const pagedEntries = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredEntries.slice(start, start + pageSize);
  }, [filteredEntries, page, pageSize]);

  const paginationMeta = useMemo<DataTablePaginationMeta>(
    () => ({
      page,
      pageSize,
      totalCount: filteredEntries.length,
      totalPages: Math.ceil(filteredEntries.length / pageSize) || 1,
      from: filteredEntries.length === 0 ? 0 : (page - 1) * pageSize + 1,
      to: Math.min(page * pageSize, filteredEntries.length),
    }),
    [page, pageSize, filteredEntries.length]
  );

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
  };

  const handleReset = () => {
    setSearchInput("");
    setAppliedSearch("");
    setPage(1);
  };

  const isFiltered = Boolean(appliedSearch);

  // Table columns definition
  const columns: DataTableColumn<CommissionEntry>[] = [
    {
      id: "id",
      header: "Txn ID",
      accessorKey: "id",
      sortable: true,
      mobilePriority: 1,
      cell: (entry) => (
        <Badge variant="outline" className="font-mono text-xs font-bold bg-muted/60">
          #TXN-{entry.id}
        </Badge>
      ),
    },
    {
      id: "product",
      header: "Product / Item",
      accessorKey: "productName",
      sortable: true,
      mobilePriority: 2,
      cell: (entry) => (
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Package className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm text-foreground line-clamp-1">
              {entry.productName}
            </p>
            {entry.channelOrderId ? (
              <p className="text-[11px] text-muted-foreground font-mono">
                Order: #ORD-{entry.channelOrderId}
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">Retail Customer Sale</p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "amount",
      header: "Commission Credited",
      accessorKey: "amount",
      align: "right",
      sortable: true,
      mobilePriority: 3,
      cell: (entry) => (
        <div className="text-right">
          <span className="inline-flex items-center gap-1 font-extrabold text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md">
            +{formatInr(entry.amount)}
          </span>
        </div>
      ),
    },
    {
      id: "balanceAfter",
      header: "Balance After",
      accessorKey: "balanceAfter",
      align: "right",
      sortable: true,
      cell: (entry) => (
        <span className="font-semibold text-sm text-foreground">
          {formatInr(entry.balanceAfter)}
        </span>
      ),
    },
    {
      id: "description",
      header: "Description / Details",
      accessorKey: "description",
      cell: (entry) => (
        <span className="text-xs text-muted-foreground line-clamp-2">
          {entry.description || "Commission credit"}
        </span>
      ),
    },
    {
      id: "date",
      header: "Credited On",
      accessorKey: "createdAt",
      sortable: true,
      cell: (entry) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatDate(entry.createdAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={`${desk.role} Commissions & Earnings`}
        description={`Your earnings as a channel partner. Commissions are credited when ${desk.earnWhen}.`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => commissionQuery.refetch()}
              className="gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Refresh
            </Button>
            <Button asChild size="sm" className="gap-1.5 shadow-sm">
              <Link to={catalogueUrl}>
                <ShoppingCart className="h-4 w-4" />
                Browse Catalogue
              </Link>
            </Button>
          </div>
        }
      />

      {/* Metrics Row */}
      <StatsGrid columns={4}>
        <CompactStatCard
          title="Current Balance"
          value={formatInr(balance)}
          icon={Coins}
          className="bg-card border-primary/20 shadow-sm"
        />
        <CompactStatCard
          title="Total Credited"
          value={formatInr(stats.totalCredited)}
          icon={IndianRupee}
          className="bg-card"
        />
        <CompactStatCard
          title="Total Transactions"
          value={stats.transactionCount}
          icon={Receipt}
          className="bg-card"
        />
        <CompactStatCard
          title="Earning Trigger"
          value={desk.role}
          icon={TrendingUp}
          className="bg-card"
        />
      </StatsGrid>

      {/* Search Bar */}
      <Card className="border border-border/80 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search transactions by Product Name, Order ID, or Description..."
                className="pl-10 h-10 w-full bg-background text-sm"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button type="submit" className="h-10 px-4 gap-2">
                <Search className="h-4 w-4" />
                Search
              </Button>
              {isFiltered && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  className="h-10 px-3 gap-1.5 text-muted-foreground hover:text-foreground"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </Button>
              )}
            </div>
          </form>

          {isFiltered && (
            <div className="flex items-center gap-2 pt-2.5 mt-2.5 border-t text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Filter:</span>
              <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                Search: &ldquo;{appliedSearch}&rdquo;
                <button
                  type="button"
                  onClick={handleReset}
                  className="ml-1 hover:text-foreground"
                >
                  ×
                </button>
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commission Ledger Table */}
      <Card className="border border-border/80 shadow-sm overflow-hidden">
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base font-bold text-foreground">
                Commission History & Ledger ({filteredEntries.length})
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Every commission credit is logged with order traceability and running balance.
              </CardDescription>
            </div>
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 gap-1 w-fit">
              <ShieldCheck className="h-3 w-3" />
              Verified Channel Credits
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable<CommissionEntry>
            columns={columns}
            data={pagedEntries}
            getRowId={(row) => String(row.id)}
            loading={commissionQuery.isLoading}
            pagination={paginationMeta}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            emptyMessage={
              isFiltered
                ? "No commission entries match your search criteria. Try resetting the search filter."
                : `No commission credited yet. Commissions are earned automatically when ${desk.earnWhen}.`
            }
            viewMode="responsive"
            cardClassName="border-0 shadow-none rounded-none"
            pageSizeOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>

      {/* Explanatory Commission Guide Card */}
      <Card className="border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold flex items-center gap-2 text-primary">
            <ShieldCheck className="h-4 w-4" />
            How {desk.role} Commissions Work
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2">
          <p>
            {desk.summary}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
            <div className="p-3 rounded-lg border bg-card/60 space-y-1">
              <span className="font-semibold text-foreground">1. Transparent Margins</span>
              <p className="text-[11px]">
                Each product in the channel catalogue has a dedicated tier percentage configured by the company.
              </p>
            </div>
            <div className="p-3 rounded-lg border bg-card/60 space-y-1">
              <span className="font-semibold text-foreground">2. Instant Ledger Credit</span>
              <p className="text-[11px]">
                Credits reflect instantly on your balance as soon as the prerequisite sale or order approval completes.
              </p>
            </div>
            <div className="p-3 rounded-lg border bg-card/60 space-y-1">
              <span className="font-semibold text-foreground">3. Full Traceability</span>
              <p className="text-[11px]">
                Every credit includes the referenced product and order ID for reliable auditing and record-keeping.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
